import { useState } from "react";
import dayjs from "dayjs";

import {
  Avatar,
  IconButton,
  Box,
  Grid,
  Typography,
  ButtonBase,
  useTheme,
  StyledEngineProvider,
  ThemeProvider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

import { DefaultPaper, CategoriesAvatar } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { mergeDarkTheme } from "@aclymatepackages/themes";
import { subcategories } from "@aclymatepackages/subcategories";

import TransactionDetailsBlock from "../../platform-pages/dashboard/TransactionDetailsBlock";
import Backdrop from "../../atoms/mui/Backdrop";

import useInputType from "../../../helpers/components/display-lists/inputTypes";
import { buildFormattedTagsObjs } from "../../../helpers/components/tags";
import { useCreateTransactions } from "../../../helpers/components/emissions";
import { useCachedFirebaseCrud } from "../../../helpers/firebase";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";

const SubcategoryButtonCard = ({
  category: { name, subcategory, sharedInputSubcategory, category, inputLabel },
  inputSubcategory,
  setInputSubcategory
}) => {
  const { palette } = useTheme();

  const [hover, setHover] = useState(false);

  const isSelected =
    inputSubcategory === subcategory ||
    (sharedInputSubcategory && inputSubcategory === sharedInputSubcategory);

  return (
    <Grid item>
      <ButtonBase
        onClick={() => setInputSubcategory(subcategory)}
        style={{ minWidth: "220px", textAlign: "left" }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        id={`manualInput-${subcategory}-option`}
      >
        <DefaultPaper
          style={{
            width: "100%",
            backgroundColor: hover && palette.backgroundGray.dark,
            outline: isSelected
              ? `thick solid ${palette.secondary.main}`
              : "none",
            position: "relative"
          }}
          elevation={hover ? 3 : 0}
        >
          {isSelected && (
            <Avatar
              style={{
                position: "absolute",
                top: -2,
                right: -2,
                backgroundColor: palette.secondary.main,
                height: 30,
                width: 30
              }}
            >
              <CheckIcon style={{ fontSize: "1em" }} />
            </Avatar>
          )}
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item>
              <CategoriesAvatar subcategory={subcategory} />
            </Grid>
            <Grid item>
              <Typography variant="body2" align="left" color="textSecondary">
                {category}
              </Typography>
              <Typography variant="h6" align="left">
                {inputLabel || name}
              </Typography>
            </Grid>
          </Grid>
        </DefaultPaper>
      </ButtonBase>
    </Grid>
  );
};

const SubcategorySelector = ({
  subcategory,
  editSubcategory,
  availableSubcategories,
  emissionsAddName
}) => {
  const { color, primaryIcon: PrimaryIcon } = useInputType("transactions");

  const inputSubcategories = subcategories.filter(({ input }) => input);

  return (
    <Grid
      container
      direction="column"
      spacing={4}
      alignItems="center"
      wrap="nowrap"
      justifyContent="center"
    >
      <Grid item>
        <Avatar
          style={{
            backgroundColor: color,
            height: 100,
            width: 100
          }}
        >
          <PrimaryIcon style={{ fontSize: "4rem" }} />
        </Avatar>
      </Grid>
      <Grid item sm={8}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={mergeDarkTheme}>
            <Typography variant="h3" color="primary" align="center">
              {`Select the category of emission that you'd like to add ${
                emissionsAddName ? `for ${emissionsAddName}` : ""
              }`}
            </Typography>
          </ThemeProvider>
        </StyledEngineProvider>
      </Grid>
      <Grid item>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          {(availableSubcategories || inputSubcategories).map(
            (category, idx) => (
              <SubcategoryButtonCard
                key={`subcategory-selection-card-${idx}`}
                category={category}
                inputSubcategory={subcategory}
                setInputSubcategory={editSubcategory}
              />
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

const ManualTransactionsInput = ({
  setDialogOpen,
  startingTransactionProps = {},
  availableSubcategories,
  emissionsAddName
}) => {
  const theme = useTheme();
  const { updateAccountData } = useCachedFirebaseCrud();
  const createTransactions = useCreateTransactions();
  const { activateSnackbar } = usePlatformLayoutContext();

  const [transaction, setTransaction] = useState({
    source: "manual-entry",
    date: new Date(),
    ...startingTransactionProps
  });
  const { subcategory, date } = transaction;

  const editTransaction = (field) => (value) =>
    editObjectData(setTransaction, field, value);

  // Anchored at UTC noon rather than the browser's own local midnight -- a
  // date picker (front-end/src/components/atoms/mui/DatePicker.js) hands
  // back local midnight of the picked day, and JSON-serializing that Date
  // bakes in the entering browser's own UTC offset. Any later bare-local
  // read of that instant (e.g. Report.js's gatherOfficeElectricityEmissions)
  // is only safe if the READER happens to share the WRITER's offset -- a
  // positive-offset entrant (Europe/Asia) has their pick land on the
  // previous UTC day for every other viewer. Noon UTC gives the same
  // +/-12h margin anchorDateOnlyStringAtNoonUtc already gives QuickBooks/
  // Plaid-synced dates (functions/utils/helperFunctions.js), independent of
  // whichever browser timezone entered it.
  const anchorPickedDayAtNoonUtc = (pickedDate) => {
    const parsedDate = dayjs(pickedDate);
    return new Date(
      Date.UTC(parsedDate.year(), parsedDate.month(), parsedDate.date(), 12)
    );
  };

  const onTransactionSave = async (transactionDetails) => {
    const mergedTransaction = {
      date: dayjs(date).toDate(),
      status: "confirmed",
      archived: false,
      ...buildFormattedTagsObjs(transaction),
      ...transaction,
      ...transactionDetails
    };

    const { writtenIds = [] } = await createTransactions([
      {
        ...mergedTransaction,
        date: anchorPickedDayAtNoonUtc(mergedTransaction.date)
      }
    ]);

    if (!writtenIds.length) {
      const message =
        "This transaction could not be saved -- it was flagged as a duplicate of an existing one.";
      activateSnackbar({ message, alert: "error" });
      throw new Error(message);
    }

    await updateAccountData({ hasUnaggregatedTransactions: true });
    return writtenIds[0];
  };

  const onSubcategorySelect = (subcategory) =>
    editTransaction("subcategory")(subcategory);

  return (
    <>
      {!subcategory ? (
        <Backdrop
          open
          style={{
            zIndex: 1300,
            color: "white",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            overflowY: "auto"
          }}
        >
          <Box
            style={{
              height: "100%",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden"
            }}
            position="relative"
            display="flex"
            alignItems="center"
            p={5}
          >
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeDarkTheme}>
                <Box
                  position="absolute"
                  top={theme.spacing(2)}
                  right={theme.spacing(2)}
                  zIndex={9999}
                >
                  <IconButton onClick={() => setDialogOpen(false)} size="large">
                    <CloseIcon size="large" />
                  </IconButton>
                </Box>
              </ThemeProvider>
            </StyledEngineProvider>
            <SubcategorySelector
              subcategory={subcategory}
              editSubcategory={onSubcategorySelect}
            />
          </Box>
        </Backdrop>
      ) : (
        <TransactionDetailsBlock
          transaction={transaction}
          editTransaction={editTransaction}
          saveTransaction={onTransactionSave}
          setIsSlideOpen={setDialogOpen}
          parentSubcategory={subcategory}
          slideLeftPanel={
            <SubcategorySelector
              subcategory={subcategory}
              editSubcategory={onSubcategorySelect}
              availableSubcategories={availableSubcategories}
              emissionsAddName={emissionsAddName}
            />
          }
          saveButtonText="Add Transaction"
        />
      )}
    </>
  );
};
export default ManualTransactionsInput;
